import { addMonths, format, isValid, parseISO } from 'date-fns';
import { apiDateTimeToLocalInput, localInputToApiDateTime } from './formatters';

export const JOB_STATUSES = [
    { value: 'em_andamento', label: 'Em andamento' },
    { value: 'sucesso', label: 'Sucesso' },
    { value: 'falha', label: 'Falha' },
    { value: 'cancelado', label: 'Cancelado' },
    { value: 'parcial', label: 'Parcial' },
    { value: 'fator_externo', label: 'Fator externo' },
];

export const STATUSES_REQUIRING_REASON = ['falha', 'cancelado', 'parcial', 'fator_externo'];

export const STATUSES_SYNC_PRINTED_COUNT = ['fator_externo'];

/** Duração padrão no cadastro de job (1 hora). */
export const DEFAULT_ESTIMATED_DURATION = '01:00';

/** inputProps para teclado numérico em mobile (MUI TextField). */
export function numericInputProps(min) {
    const props = { inputMode: 'numeric' };
    if (min !== undefined) {
        props.min = min;
    }
    return props;
}

export function statusLabel(value) {
    const found = JOB_STATUSES.find((s) => s.value === value);
    return found ? found.label : value || '—';
}

const JOB_STATUS_BADGE = {
    em_andamento: { label: 'Em andamento', color: 'info' },
    sucesso: { label: 'Sucesso', color: 'success' },
    parcial: { label: 'Parcial', color: 'warning' },
    falha: { label: 'Falha', color: 'error' },
    fator_externo: { label: 'Fator externo', color: 'secondary' },
    cancelado: {
        label: 'Cancelado',
        sx: {
            bgcolor: '#ed6c02',
            color: '#fff',
            fontWeight: 600,
        },
    },
};

/** Props para MUI Chip na listagem de jobs. */
export function getJobStatusBadgeProps(status) {
    const config = JOB_STATUS_BADGE[status];
    if (!config) {
        return { label: statusLabel(status), size: 'small', color: 'default' };
    }
    return { label: config.label, size: 'small', color: config.color, sx: config.sx };
}

export function filamentsToColors(filaments) {
    if (!Array.isArray(filaments)) {
        return [''];
    }
    const colors = filaments.map((f) => (f?.color ?? '').trim()).filter(Boolean);
    return colors.length ? colors : [''];
}

export function colorsToFilaments(colors) {
    return colors
        .map((c) => c.trim())
        .filter(Boolean)
        .map((color) => ({ color }));
}

export function toInputDateTime(dbValue) {
    return apiDateTimeToLocalInput(dbValue) || '';
}

/** Converte minutos (API) para HH:MM (formulário). */
export function minutesToDuration(totalMinutes) {
    const total = Math.max(0, parseInt(totalMinutes, 10) || 0);
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Converte HH:MM para minutos (API), mesma lógica do TimeInputWithBlur / calculadora.
 * Retorna null se os minutos forem inválidos (> 59).
 */
export function durationToMinutes(value) {
    if (value == null || String(value).trim() === '') {
        return 0;
    }
    const cleaned = String(value).replace(/\D/g, '');
    if (cleaned === '') {
        return 0;
    }
    const hours = parseInt(cleaned.slice(0, 2), 10) || 0;
    const minutes = parseInt(cleaned.slice(2, 4), 10) || 0;
    if (minutes > 59) {
        return null;
    }
    return hours * 60 + minutes;
}

/** Período máximo do filtro de datas na listagem de impressões. */
export const JOBS_DATE_FILTER_MAX_MONTHS = 1;

export function parseJobsDateInput(value) {
    if (!value || !String(value).trim()) {
        return null;
    }
    const day = String(value).trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
        return null;
    }
    const parsed = parseISO(day);
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null;
}

/** Data final máxima (inclusive) para um início informado — no máximo 1 mês depois. */
export function maxJobsDateRangeEnd(startDate) {
    const start = parseJobsDateInput(startDate);
    if (!start) {
        return '';
    }
    return format(addMonths(parseISO(start), JOBS_DATE_FILTER_MAX_MONTHS), 'yyyy-MM-dd');
}

/**
 * Normaliza início/fim do filtro: ordena, preenche dia único e limita a 1 mês.
 */
export function clampJobsDateRange(startDate, endDate) {
    let start = parseJobsDateInput(startDate);
    let end = parseJobsDateInput(endDate);

    if (!start && !end) {
        return { start: '', end: '' };
    }
    if (start && !end) {
        end = start;
    }
    if (!start && end) {
        start = end;
    }
    if (start > end) {
        [start, end] = [end, start];
    }

    const maxEnd = maxJobsDateRangeEnd(start);
    if (maxEnd && end > maxEnd) {
        end = maxEnd;
    }

    return { start, end };
}

/** Ordena datas do filtro sem limitar a 1 mês (taxa de sucesso / printer-stats). */
export function normalizeJobsDateRange(startDate, endDate) {
    let start = parseJobsDateInput(startDate);
    let end = parseJobsDateInput(endDate);

    if (!start && !end) {
        return { start: '', end: '' };
    }
    if (start && !end) {
        end = start;
    }
    if (!start && end) {
        start = end;
    }
    if (start > end) {
        [start, end] = [end, start];
    }

    return { start, end };
}

/** Período para printer-stats: sem limite de 1 mês. */
export function jobsDateRangeForStatsApi(startDate, endDate) {
    const { start, end } = normalizeJobsDateRange(startDate, endDate);
    if (!start && !end) {
        return { finished_from: null, finished_to: null, error: null };
    }
    if (!start || !end) {
        return {
            finished_from: null,
            finished_to: null,
            error: 'Informe a data inicial e a data final.',
        };
    }

    return {
        finished_from: start,
        finished_to: end,
        error: null,
    };
}

export const PRINTER_STATS_PERIOD_MODES = [
    { value: 'all_time', label: 'Todo o tempo' },
    { value: 'list_filters', label: 'Filtros da lista' },
    { value: 'custom', label: 'Período específico' },
];

export function buildPrinterStatsQuery({ printerUuid, status, finishedFrom, finishedTo, q } = {}) {
    const params = new URLSearchParams();
    if (q) {
        params.set('q', q);
    }
    if (printerUuid) {
        params.set('printer_uuid', printerUuid);
    }
    if (status) {
        params.set('status', status);
    }
    if (finishedFrom) {
        params.set('finished_from', finishedFrom);
    }
    if (finishedTo) {
        params.set('finished_to', finishedTo);
    }
    return params.toString();
}

export function resolvePrinterStatsPeriod({
    mode,
    queryFilters,
    customFrom,
    customTo,
}) {
    if (mode === 'all_time') {
        return {
            printerUuid: '',
            status: '',
            finishedFrom: '',
            finishedTo: '',
            error: null,
        };
    }

    if (mode === 'list_filters') {
        return {
            printerUuid: queryFilters?.printer_uuid || '',
            status: queryFilters?.status || '',
            finishedFrom: queryFilters?.finished_from || '',
            finishedTo: queryFilters?.finished_to || '',
            error: null,
        };
    }

    const range = jobsDateRangeForStatsApi(customFrom, customTo);
    if (range.error) {
        return { error: range.error };
    }

    return {
        printerUuid: '',
        status: '',
        finishedFrom: range.finished_from || '',
        finishedTo: range.finished_to || '',
        error: null,
    };
}

/** Converte intervalo de datas do filtro em parâmetros da API (YYYY-MM-DD). */
export function jobsDateRangeForApi(startDate, endDate) {
    const { start, end } = clampJobsDateRange(startDate, endDate);
    if (!start && !end) {
        return { finished_from: null, finished_to: null, error: null };
    }
    if (!start || !end) {
        return {
            finished_from: null,
            finished_to: null,
            error: 'Informe a data inicial e a data final.',
        };
    }

    return {
        finished_from: start,
        finished_to: end,
        error: null,
    };
}

export function buildJobsFilterQuery({ q, printerUuid, status, finishedFrom, finishedTo }) {
    const params = new URLSearchParams();
    if (q) {
        params.set('q', q);
    }
    if (printerUuid) {
        params.set('printer_uuid', printerUuid);
    }
    if (status) {
        params.set('status', status);
    }
    if (finishedFrom) {
        params.set('finished_from', finishedFrom);
    }
    if (finishedTo) {
        params.set('finished_to', finishedTo);
    }
    return params.toString();
}

export function hasActiveJobsQueryFilters(queryFilters) {
    return Boolean(
        queryFilters?.printer_uuid
        || queryFilters?.status
        || queryFilters?.finished_from
        || queryFilters?.finished_to,
    );
}

/** Parâmetros compartilhados entre listagem e printer-stats. */
export function buildSharedJobsQueryParams(queryFilters, { searchTerm = '', appliedSearchTerm = false } = {}) {
    return {
        printerUuid: queryFilters?.printer_uuid || '',
        status: queryFilters?.status || '',
        finishedFrom: queryFilters?.finished_from || '',
        finishedTo: queryFilters?.finished_to || '',
        q: appliedSearchTerm ? (searchTerm || '').trim() : '',
    };
}

export function resolveEffectiveStatsPeriodMode(statsPeriodMode, queryFilters) {
    if (hasActiveJobsQueryFilters(queryFilters)) {
        return 'list_filters';
    }
    return statsPeriodMode;
}

export function resolvePrinterStatsQueryParams({
    mode,
    queryFilters,
    customFrom,
    customTo,
    searchTerm,
    appliedSearchTerm,
}) {
    const period = resolvePrinterStatsPeriod({ mode, queryFilters, customFrom, customTo });
    if (period.error) {
        return { error: period.error };
    }

    const shared = buildSharedJobsQueryParams(queryFilters, { searchTerm, appliedSearchTerm });

    if (mode === 'all_time') {
        return {
            error: null,
            params: {
                printerUuid: '',
                status: '',
                finishedFrom: '',
                finishedTo: '',
                q: '',
            },
        };
    }

    if (mode === 'custom') {
        return {
            error: null,
            params: {
                q: '',
                printerUuid: '',
                status: '',
                finishedFrom: period.finishedFrom || '',
                finishedTo: period.finishedTo || '',
            },
        };
    }

    return {
        error: null,
        params: shared,
    };
}

export function sumPrinterStatsJobs(stats) {
    return (stats || []).reduce((acc, row) => acc + (Number(row?.total_jobs) || 0), 0);
}

export function buildJobsListQuery({ page, limit, q, printerUuid, status, finishedFrom, finishedTo }) {
    const params = new URLSearchParams(buildJobsFilterQuery({
        q,
        printerUuid,
        status,
        finishedFrom,
        finishedTo,
    }));
    params.set('page', String(page));
    params.set('limit', String(limit));
    return params.toString();
}

/** Monta cards de impressoras com taxa de sucesso (ordem de cadastro preservada). */
export function buildPrinterSuccessCards(printers, stats, inProgressSet = new Set()) {
    const statsMap = Object.fromEntries((stats || []).map((item) => [item.printer_uuid, item]));
    const printerIds = new Set((printers || []).map((p) => p.id));

    const cards = (printers || []).map((printer) => {
        const stat = statsMap[printer.id];
        return {
            printer_uuid: printer.id,
            name: printer.name,
            brand: printer.brand,
            success_rate: stat?.success_rate ?? 0,
            total_jobs: stat?.total_jobs ?? 0,
            total_objects: stat?.total_objects ?? 0,
            total_printed: stat?.total_printed ?? 0,
            total_duration_minutes: stat?.total_duration_minutes ?? 0,
            total_filament_grams: stat?.total_filament_grams ?? 0,
            is_printing: inProgressSet.has(printer.id),
        };
    });

    (stats || []).forEach((stat) => {
        if (!stat?.printer_uuid || printerIds.has(stat.printer_uuid)) {
            return;
        }
        cards.push({
            printer_uuid: stat.printer_uuid,
            name: stat.printer_uuid,
            brand: '',
            success_rate: stat.success_rate ?? 0,
            total_jobs: stat.total_jobs ?? 0,
            total_objects: stat.total_objects ?? 0,
            total_printed: stat.total_printed ?? 0,
            total_duration_minutes: stat.total_duration_minutes ?? 0,
            total_filament_grams: stat.total_filament_grams ?? 0,
            is_printing: inProgressSet.has(stat.printer_uuid),
        });
    });

    return cards;
}

/** Duração de produção legível (ex.: 42h 30min). */
export function formatProductionDurationMinutes(totalMinutes) {
    const minutes = Math.max(0, Math.round(Number(totalMinutes)) || 0);
    if (minutes === 0) {
        return '0min';
    }
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (hours === 0) {
        return `${remainder}min`;
    }
    if (remainder === 0) {
        return `${hours}h`;
    }
    return `${hours}h ${remainder}min`;
}

/** Filamento estimado em gramas ou quilos. */
export function formatFilamentGrams(grams) {
    const value = Math.max(0, Math.round(Number(grams)) || 0);
    if (value >= 1000) {
        const kg = value / 1000;
        return `${kg.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`;
    }
    return `${value.toLocaleString('pt-BR')} g`;
}

export function summarizePrinterStatsProduction(stats) {
    return (stats || []).reduce((acc, row) => ({
        total_duration_minutes: acc.total_duration_minutes + (Number(row?.total_duration_minutes) || 0),
        total_filament_grams: acc.total_filament_grams + (Number(row?.total_filament_grams) || 0),
        total_jobs: acc.total_jobs + (Number(row?.total_jobs) || 0),
        total_objects: acc.total_objects + (Number(row?.total_objects) || 0),
        total_printed: acc.total_printed + (Number(row?.total_printed) || 0),
    }), {
        total_duration_minutes: 0,
        total_filament_grams: 0,
        total_jobs: 0,
        total_objects: 0,
        total_printed: 0,
    });
}

export function successRateFromTotals(totalPrinted, totalObjects) {
    if (!totalObjects) {
        return 0;
    }
    return Math.round((totalPrinted / totalObjects) * 1000) / 10;
}

export function toApiDateTime(inputValue) {
    return localInputToApiDateTime(inputValue);
}

export function jobFromApi(data) {
    return {
        printer_uuid: data.printer_uuid || '',
        file_name: data.file_name || '',
        estimated_duration: minutesToDuration(data.estimated_duration_minutes ?? 0),
        estimated_filament_consumption_grams: data.estimated_filament_consumption_grams ?? 0,
        started_at: toInputDateTime(data.started_at),
        finished_at: toInputDateTime(data.finished_at),
        status: data.status || 'sucesso',
        status_reason: data.status_reason || '',
        object_count: data.object_count ?? 1,
        printed_count: data.printed_count ?? 0,
    };
}

export function buildJobPayload(form, filamentColors) {
    const payload = {
        printer_uuid: form.printer_uuid,
        file_name: form.file_name,
        estimated_duration_minutes: durationToMinutes(form.estimated_duration) ?? 0,
        estimated_filament_consumption_grams: parseInt(form.estimated_filament_consumption_grams, 10) || 0,
        status: form.status,
        object_count: parseInt(form.object_count, 10) || 1,
        printed_count: parseInt(form.printed_count, 10) || 0,
    };

    const started = toApiDateTime(form.started_at);
    const finished = toApiDateTime(form.finished_at);
    if (started) {
        payload.started_at = started;
    }
    if (finished) {
        payload.finished_at = finished;
    }

    if (STATUSES_REQUIRING_REASON.includes(form.status) && form.status_reason?.trim()) {
        payload.status_reason = form.status_reason.trim();
    }

    const filaments = colorsToFilaments(filamentColors);
    if (filaments.length) {
        payload.filaments = filaments;
    }

    return payload;
}
